import type { SxProps, Theme } from "@mui/material";
import { styled, TextField } from "@mui/material";

interface CanvasFormTextFieldProps {
	width?: string;
}

// Shared sx so rule-builder <Select> fields (Data type, Operator, Resource,
// Value, etc.) match CanvasFormTextField EXACTLY — same border, focus ring,
// label, and crucially the same 10px/12px input padding so the placeholder /
// value text sits on the same vertical centerline as the text inputs beside
// them (fixes the "Data type / Operator not aligned" misalignment).
// App-standard scrollbar — WHITE trough + brand-teal thin pill thumb. Matches
// the global scrollbar style (see APP_SCROLLBAR_GLOBAL in index) so rule-builder
// scroll containers (form bodies, toolbox) look identical to every other
// scrollbar in the app.
export const appScrollbarSx: SxProps<Theme> = {
	"&::-webkit-scrollbar, & *::-webkit-scrollbar": {
		backgroundColor: "#FFFFFF",
		width: "12px",
		height: "12px",
	},
	"&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
		backgroundColor: "#FFFFFF",
	},
	"&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
		borderRadius: "999px",
		backgroundColor: "rgba(2, 213, 171, 0.55)",
		border: "3px solid #FFFFFF",
		backgroundClip: "content-box",
	},
	"&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
		backgroundColor: "rgba(2, 213, 171, 0.85)",
	},
};

export const canvasSelectSx: SxProps<Theme> = {
	"& .MuiOutlinedInput-root": {
		borderRadius: "8px",
		background: "#FFFFFF",
		fontSize: 14,
		fontWeight: 500,
		transition: "border-color 0.18s, box-shadow 0.18s",
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: "rgba(13, 31, 48, 0.14)",
			borderWidth: 1,
			transition: "border-color 0.18s",
		},
		"&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(2, 213, 171, 0.5)" },
		"&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#02D5AB", borderWidth: 1 },
		"&.Mui-focused": { boxShadow: "0 0 0 3px rgba(2, 213, 171, 0.18)" },
		"&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#E03030" },
	},
	// The control that actually holds the selected text / placeholder.
	"& .MuiSelect-select": {
		padding: "10px 12px",
		display: "flex",
		alignItems: "center",
		minHeight: "21px !important",
		boxSizing: "border-box",
	},
	"& .MuiInputLabel-root": {
		fontSize: 13,
		fontWeight: 500,
		color: "rgba(13, 31, 48, 0.55)",
		"&.Mui-focused": { color: "#057A6D" },
	},
};

// Outlined text field used inside rule-builder node forms (Decision, Query,
// Filter, etc). Brand teal focus ring + hover, soft border, generous padding.
//
export const CanvasFormTextField = styled(TextField)<CanvasFormTextFieldProps>(({ width }) => ({
	width: width,
	"& .MuiOutlinedInput-root": {
		borderRadius: 8,
		background: "#FFFFFF",
		fontSize: 14,
		fontWeight: 500,
		transition: "border-color 0.18s, box-shadow 0.18s",
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: "rgba(13, 31, 48, 0.14)",
			borderWidth: 1,
			transition: "border-color 0.18s"
		},
		"&:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: "rgba(2, 213, 171, 0.5)"
		},
		"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: "#02D5AB",
			borderWidth: 1
		},
		"&.Mui-focused": {
			boxShadow: "0 0 0 3px rgba(2, 213, 171, 0.18)"
		},
		"&.Mui-error .MuiOutlinedInput-notchedOutline": {
			borderColor: "#E03030"
		},
		"&.Mui-error.Mui-focused": {
			boxShadow: "0 0 0 3px rgba(224, 48, 48, 0.18)"
		},
		"&.Mui-disabled": {
			background: "rgba(13, 31, 48, 0.03)",
			"& .MuiOutlinedInput-notchedOutline": {
				borderColor: "rgba(13, 31, 48, 0.08)"
			}
		}
	},
	"& .MuiInputLabel-root": {
		fontSize: 13,
		fontWeight: 500,
		color: "rgba(13, 31, 48, 0.55)",
		"&.Mui-focused": {
			color: "#057A6D"
		}
	},
	"& .MuiOutlinedInput-input": {
		padding: "10px 12px"
	}
}));